
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Plus } from "lucide-react"
import { FormModal } from "@/components/ui/form-modal"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@/hooks/useAuth"
import { useDestinatariosWhatsApp, useEnviarWhatsApp, useWhatsAppConnection } from "@/hooks/useWhatsApp"
import { useEffect } from "react"
import { MessageCircle } from "lucide-react"

interface PublicarAvisoModalProps {
  children?: React.ReactNode
  announcement?: any
  isEditing?: boolean
  onClose?: () => void
}

const PublicarAvisoModal = ({ children, announcement, isEditing = false, onClose }: PublicarAvisoModalProps) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [title, setTitle] = useState(announcement?.title || "")
  const [content, setContent] = useState(announcement?.content || "")
  const [isImportant, setIsImportant] = useState(announcement?.is_important || false)
  const [targetAudience, setTargetAudience] = useState<'all' | 'residents' | 'staff'>(
    announcement?.target_audience || 'all'
  )
  const [expiresAt, setExpiresAt] = useState(announcement?.expires_at ? announcement.expires_at.split('T')[0] : "")
  
  const { toast } = useToast()
  const { profile } = useAuth()

  // envio automático pelo WhatsApp do condomínio
  const { conectado: whatsappConectado } = useWhatsAppConnection()
  const { buscar: buscarDestinatarios } = useDestinatariosWhatsApp()
  const { enviar: enviarWhatsApp } = useEnviarWhatsApp()
  const [enviarNoWhatsApp, setEnviarNoWhatsApp] = useState(true)
  const [qtdDestinatarios, setQtdDestinatarios] = useState(0)

  // mostra quantas pessoas vão receber, conforme o público-alvo escolhido
  useEffect(() => {
    if (!open || isEditing || !whatsappConectado) return
    let ativo = true
    buscarDestinatarios(targetAudience).then((numeros) => {
      if (ativo) setQtdDestinatarios(numeros.length)
    })
    return () => {
      ativo = false
    }
  }, [open, isEditing, whatsappConectado, targetAudience, buscarDestinatarios])
  
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // QueryClient not available, continue without it
    queryClient = null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!profile) return
    
    setLoading(true)

    try {
    const announcementData = {
      title,
      content,
      author_id: profile.id,
      is_important: isImportant,
      target_audience: targetAudience,
      expires_at: expiresAt 
        ? new Date(expiresAt + "T23:59:59").toISOString() 
        : null
    }

      if (isEditing && announcement) {
        const { error } = await supabase
          .from("announcements")
          .update(announcementData)
          .eq("id", announcement.id)
        
        if (error) throw error
        
        toast({
          title: "Sucesso!",
          description: "Aviso atualizado com sucesso.",
        })
      } else {
        const { error } = await supabase
          .from("announcements")
          .insert(announcementData)

        if (error) throw error

        toast({
          title: "Sucesso!",
          description: "Aviso publicado com sucesso.",
        })

        // Dispara o aviso no WhatsApp do condomínio
        if (enviarNoWhatsApp && whatsappConectado) {
          const numeros = await buscarDestinatarios(targetAudience)

          const seguirEnvio =
            numeros.length <= 30 ||
            confirm(
              `Este aviso será enviado no WhatsApp para ${numeros.length} pessoas.\n\n` +
                `O envio é feito com intervalo entre as mensagens para proteger o número. Confirmar?`,
            )

          if (seguirEnvio && numeros.length > 0) {
            const texto =
              `${isImportant ? "🔴 *AVISO IMPORTANTE*" : "📢 *AVISO DO CONDOMÍNIO*"}\n\n` +
              `*${title}*\n\n${content}\n\n` +
              `_Mensagem automática do sistema do condomínio_`

            await enviarWhatsApp({ numeros, texto, origem: "aviso" })
          }
        }
      }

      // Reset form
      setTitle("")
      setContent("")
      setIsImportant(false)
      setTargetAudience('all')
      setExpiresAt("")
      setOpen(false)
      
      if (onClose) onClose()

      // Refresh data if QueryClient is available
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ["announcements"] })
        queryClient.invalidateQueries({ queryKey: ["notifications"] })
        queryClient.invalidateQueries({ queryKey: ["stats"] })
      }
    } catch (error: any) {
      console.error("❌ Erro detalhado ao publicar aviso:", {
        error,
        errorMessage: error.message,
        errorCode: error.code,
        errorDetails: error.details,
        errorHint: error.hint,
        profile: {
          id: profile?.id,
          role: profile?.role,
          condominium_id: profile?.condominium_id
        },
        announcementData: {
          title,
          content,
          target_audience: targetAudience,
          is_important: isImportant,
          expires_at: expiresAt
        }
      })
      
      toast({
        title: "Erro ao Publicar Aviso",
        description: error.message || error.hint || "Erro desconhecido. Verifique o console para mais detalhes.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImportantChange = (checked: boolean | "indeterminate") => {
    setIsImportant(checked === true)
  }

  const formContent = (
    <>
      <div className="grid gap-2">
        <Label htmlFor="title">Título *</Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="content">Conteúdo *</Label>
        <Textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={4}
          required
        />
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="important"
          checked={isImportant}
          onCheckedChange={handleImportantChange}
        />
        <Label htmlFor="important">Aviso importante</Label>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="target_audience">Público-alvo *</Label>
        <Select value={targetAudience} onValueChange={(value: 'all' | 'residents' | 'staff') => setTargetAudience(value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">🌐 Todos (Moradores + Trabalhadores)</SelectItem>
            <SelectItem value="residents">🏠 Apenas Moradores</SelectItem>
            <SelectItem value="staff">👷 Apenas Trabalhadores (Porteiros, Administração)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {!isEditing && whatsappConectado && (
        <div className="flex items-start space-x-2 rounded-md border bg-muted/40 p-3">
          <Checkbox
            id="enviar_whatsapp"
            checked={enviarNoWhatsApp}
            onCheckedChange={(checked) => setEnviarNoWhatsApp(checked === true)}
          />
          <div className="grid gap-1 leading-none">
            <Label htmlFor="enviar_whatsapp" className="flex items-center gap-1.5">
              <MessageCircle className="h-3.5 w-3.5" />
              Enviar também no WhatsApp
            </Label>
            <p className="text-xs text-muted-foreground">
              {qtdDestinatarios > 0
                ? `${qtdDestinatarios} ${qtdDestinatarios === 1 ? "pessoa vai receber" : "pessoas vão receber"} este aviso`
                : "Nenhum número de WhatsApp cadastrado para este público"}
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-2">
        <Label htmlFor="expires_at">Data de Expiração (opcional)</Label>
        <Input
          id="expires_at"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
        />
      </div>
    </>
  )

  if (isEditing) {
    return (
      <FormModal
        open={true}
        onOpenChange={onClose || (() => {})}
        title="Editar Aviso"
        description="Edite as informações do aviso."
        onSubmit={handleSubmit}
        submitButtonText="Salvar Alterações"
        loading={loading}
        formContent={formContent}
      />
    )
  }

  return (
    <FormModal
      open={open}
      onOpenChange={setOpen}
      title="Publicar Aviso"
      description="Publique um novo aviso para todos os moradores."
      onSubmit={handleSubmit}
      submitButtonText="Publicar Aviso"
      loading={loading}
      formContent={formContent}
      triggerButton={
        <Button className="w-full justify-start">
          <Plus className="h-4 w-4 mr-2" />
          Publicar novo aviso
        </Button>
      }
    >
      {children}
    </FormModal>
  )
}

export default PublicarAvisoModal
